/**
 * Client-side Sentry flood guard.
 *
 * Why this exists: the Sentry browser client has no `sampleRate`, no
 * `beforeSend`, and no rate limit, so a single hydration storm on the search
 * results page (up to ~50 identical React #418 text-node mismatches per page
 * load — see `ListProductCard` dates / TASK-INSTRUMENTATION-PACKAGE §I5) ships
 * every copy. Sentry's ingest quota answers with 429, and 429s apply to the
 * whole PROJECT, so unrelated errors from checkout, cart, and payments are
 * silently dropped. The org has accepted zero events for days as a result.
 *
 * Design choice — targeted dedup, NOT a blanket `sampleRate`:
 * a global sample rate would throw away rare, unique real errors (a one-off
 * checkout failure has the same odds of being dropped as one copy of a storm).
 * Instead we cap how many times ONE fingerprint may be sent inside a rolling
 * time window. A storm collapses to a handful of events; a unique
 * checkout/cart error is a distinct fingerprint and always reaches Sentry.
 *
 * Design choice — a time WINDOW, not a lifetime count:
 * this module lives at module scope, which survives Next.js soft navigations,
 * so a plain lifetime counter would permanently silence a real, recurring
 * error after N occurrences across an entire multi-page session. The window
 * is TUMBLING, not rolling: `windowStart` is fixed at the first sighting of a
 * fingerprint and does not slide forward on each subsequent hit, so a burst
 * that straddles a window boundary (e.g. copies land just before AND just
 * after `windowStart + windowMs`) can pass up to 2x `maxPerWindow` through.
 * That's an accepted tradeoff for simplicity; it still bounds the storm to a
 * small multiple, and a genuinely recurring error reports again once a fresh
 * window opens.
 *
 * This is NOT a blocklist (unlike `ignoreErrors`): nothing is filtered by
 * identity, so a brand-new hydration bug — or the before/after signal the
 * #1438 hydration fix needs — still reports. We keep the first N copies and
 * annotate the last kept one so the flood is visible without being unbounded.
 *
 * Sentry's built-in `Dedupe` integration (enabled by default in the v7 browser
 * SDK; the config passes no `integrations`, so it stays on) only drops events
 * identical to the IMMEDIATELY PREVIOUS one. An interleaved storm slips past it,
 * which is exactly the gap this guard closes.
 */

/** Minimal structural shape of a Sentry stack frame — kept decoupled from the
 *  SDK so this module is trivially unit-testable without importing
 *  `@sentry/nextjs`. */
export interface SentryStackFrameLike {
  function?: string;
  filename?: string;
  in_app?: boolean;
}

/** Minimal structural shape of a Sentry event — kept decoupled from the SDK so
 *  this module is trivially unit-testable without importing `@sentry/nextjs`. */
export interface SentryEventLike {
  message?: string;
  extra?: Record<string, unknown>;
  tags?: Record<string, string>;
  exception?: {
    values?: Array<{
      type?: string;
      value?: string;
      stacktrace?: { frames?: SentryStackFrameLike[] };
    }>;
  };
}

/**
 * Sentry tag a caller may set to add BOUNDED variance to the flood-guard
 * identity. It does not touch Sentry's own grouping — Sentry groups by
 * stacktrace and never reads this tag — so a distinct value here splits
 * flood-guard buckets without splitting the Sentry issue.
 *
 * Needed because some error families are genuinely distinct while sharing one
 * `type:value|frame`. GB #1527: every shipping-rate failure in a cart throws the
 * same message from the same frame, so a 20-item cart's twenty distinct vendor
 * response bodies collapsed to the 5 this guard lets through and the rest of the
 * diagnostics were dropped. `logError` sets this to a hash of the redacted
 * vendor body, so identical diagnostics still collapse and different ones don't.
 *
 * Whatever is set here is truncated to 32 chars in the fingerprint, and the
 * guard already bounds tracked fingerprints to `maxKeys` with pruning, so a
 * high-cardinality value cannot grow the map without limit.
 */
export const FLOOD_VARIANCE_TAG = 'flood_variance';

export interface FloodGuardOptions {
  /** Max copies of one fingerprint sent per rolling window. Default 5. */
  maxPerWindow?: number;
  /** Rolling window length in ms. Default 10_000 (10s). */
  windowMs?: number;
  /** Cap on tracked fingerprints before expired ones are pruned. Default 200. */
  maxKeys?: number;
  /** Clock injection point for tests. Default `Date.now`. */
  now?: () => number;
}

interface WindowRecord {
  count: number;
  windowStart: number;
}

/**
 * Pick the frame that identifies WHERE in the code the exception happened, so
 * two different bugs that happen to share a generic `type:value` (e.g. two
 * unrelated `TypeError: Cannot read properties of undefined`) don't collapse
 * into one fingerprint and let one suppress the other. Sentry's frame array is
 * ordered oldest-to-newest, so the "top" (most specific) frame is the last
 * one; we search from the end for the last frame marked `in_app` (application
 * code, not a vendor/framework frame) and fall back to the very first frame if
 * none is marked in_app.
 */
const findKeyFrame = (
  frames: SentryStackFrameLike[] | undefined,
): SentryStackFrameLike | undefined => {
  if (!frames || frames.length === 0) return undefined;
  for (let i = frames.length - 1; i >= 0; i -= 1) {
    if (frames[i]?.in_app) return frames[i];
  }
  return frames[0];
};

/**
 * Collapse an event to a stable identity. React minified errors (#418/#423/#425)
 * each carry a constant `value` string per code, so every copy of a storm maps
 * to one key while distinct real errors keep distinct keys.
 *
 * Exception chains: @sentry/nextjs 7.120.4 PREPENDS cause-chain exceptions, so
 * `values[0]` is the deepest CAUSE, not the error that was actually thrown.
 * We key on `values[values.length - 1]` — the originally-thrown error — to
 * match what a human reading the Sentry issue title actually sees.
 *
 * When a stacktrace is present we also fold in the key frame's
 * function+filename, so two different bugs with a shared generic message
 * don't share a bucket (see `findKeyFrame`).
 */
export const fingerprintEvent = (event: SentryEventLike): string => {
  // Optional caller-supplied variance (see FLOOD_VARIANCE_TAG).
  const variance = event?.tags?.[FLOOD_VARIANCE_TAG];
  const suffix = variance ? `#${String(variance).slice(0, 32)}` : '';

  const values = event?.exception?.values;
  const last = values && values.length > 0 ? values[values.length - 1] : undefined;
  if (last && (last.type || last.value)) {
    const base = `${last.type ?? ''}:${last.value ?? ''}`;
    const frame = findKeyFrame(last.stacktrace?.frames);
    if (frame) {
      return `${base}|${frame.function ?? ''}@${frame.filename ?? ''}${suffix}`;
    }
    return `${base}${suffix}`;
  }
  return `${event?.message ?? 'unknown'}${suffix}`;
};

/**
 * Build a `beforeSend` handler that rate-limits per fingerprint. Exported as a
 * factory so tests can inject a clock and thresholds; the config below uses the
 * default-configured singleton.
 */
export const createSentryFloodGuard = (options: FloodGuardOptions = {}) => {
  const maxPerWindow = options.maxPerWindow ?? 5;
  const windowMs = options.windowMs ?? 10_000;
  const maxKeys = options.maxKeys ?? 200;
  const now = options.now ?? Date.now;

  const suppressionNote = `further identical copies suppressed for this ${windowMs / 1000}s window`;

  const seen = new Map<string, WindowRecord>();

  const pruneExpired = (ts: number): void => {
    for (const [key, rec] of seen) {
      if (ts - rec.windowStart > windowMs) seen.delete(key);
    }
  };

  return <T extends SentryEventLike>(event: T): T | null => {
    // Fail OPEN: the guard must never drop an error because the guard itself
    // threw on an unexpected event shape.
    try {
      if (!event) return event;

      const ts = now();
      const key = fingerprintEvent(event);
      const rec = seen.get(key);

      // First sighting of this fingerprint, or its window has elapsed: reset.
      if (!rec || ts - rec.windowStart > windowMs) {
        if (seen.size >= maxKeys) pruneExpired(ts);
        seen.set(key, { count: 1, windowStart: ts });
        return event;
      }

      rec.count += 1;
      if (rec.count > maxPerWindow) return null;
      if (rec.count === maxPerWindow) {
        if (event.exception) {
          // Exception events group by stack, not by message, so annotating
          // the message is safe here — it can't create a spurious new issue.
          event.message = event.message
            ? `${event.message} [${suppressionNote}]`
            : `[${suppressionNote}]`;
        } else {
          // Sentry groups MESSAGE events (captureMessage) by message text.
          // Mutating event.message here would make the annotated copy a
          // NEW, distinct Sentry issue mid-storm (and could fire a
          // new-issue alert). Record the note out-of-band instead.
          event.extra = { ...event.extra, flood_guard_note: suppressionNote };
          event.tags = { ...event.tags, flood_guard: 'capped' };
        }
      }
      return event;
    } catch {
      return event;
    }
  };
};

/** Default-configured guard used by `sentry.client.config.js`. */
export const sentryFloodGuardBeforeSend = createSentryFloodGuard();
